import { normalizeTime, zonedToInstant } from './bookingTime.js';
import { parseFlightNumber } from './flightNumber.js';

export const RESERVATION_SERVICES = Object.freeze([
    { code: 'AX', label: 'Airport Transfer' },
    { code: 'P2P', label: 'Point-to-Point' },
    { code: 'CH', label: 'Charter' },
]);
export const RESERVATION_STEPS = ['Trip', 'Riders', 'Review & request'];
export const RESERVATION_BASE_REQUIRED = ['serviceType', 'date', 'time', 'passengers', 'pickup'];
export const EMPTY_RESERVATION = {
    serviceType: '', direction: 'departing', airportCode: '', date: '', time: '', passengers: 1, bags: '',
    airline: '', flightNumber: '', flightTime: '', terminal: 'domestic', pickup: '', dropoff: '', hours: '',
    bookingForOther: false, fullName: '', mobile: '', email: '', roundtrip: false, sameLocation: true,
    returnDate: '', returnTime: '', returnPickup: '', returnDropoff: '', returnAirline: '', returnFlightNumber: '',
    returnFlightTime: '', stops: [], childSeats: 0, notes: '',
};

export function airportDropoffPickup(date, flightTime, leadMinutes = 150, timeZone = 'America/New_York') {
    const time = normalizeTime(flightTime);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !time || !Number.isInteger(leadMinutes) || leadMinutes < 0) return null;
    const departure = new Date(`${date}T${time}:00Z`);
    if (Number.isNaN(departure.getTime()) || departure.toISOString().slice(0, 10) !== date) return null;
    const pickup = new Date(zonedToInstant(date, time, timeZone) - leadMinutes * 60000);
    if (Number.isNaN(pickup.getTime())) return null;
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(pickup).map((part) => [part.type, part.value]));
    return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

export function reservationRules(fields = {}, { knownClient = true, charterMinimumHours = 3 } = {}) {
    const airport = fields.serviceType === 'AX';
    const charter = fields.serviceType === 'CH';
    const contact = !knownClient || fields.bookingForOther;
    return {
        airport, charter, contact, email: !knownClient && !fields.bookingForOther,
        arriving: airport && fields.direction === 'arriving',
        extras: fields.serviceType === 'P2P' || charter,
        charterMinimumHours,
        required: [
            ...RESERVATION_BASE_REQUIRED,
            ...(!charter || (fields.roundtrip && fields.sameLocation) ? ['dropoff'] : []),
            ...(charter ? ['hours'] : []),
            ...(airport ? ['direction', 'airportCode'] : []),
            ...(airport && fields.direction === 'arriving' ? ['airline', 'flightNumber'] : []),
            ...(airport && fields.direction !== 'arriving' && fields.flightNumber ? ['airline'] : []),
            ...(contact ? ['fullName', 'mobile'] : []),
            ...(!knownClient && !fields.bookingForOther ? ['email'] : []),
            ...(fields.roundtrip ? ['returnDate', 'returnTime',
                ...(!fields.sameLocation ? ['returnPickup', ...(!charter ? ['returnDropoff'] : [])] : []),
                ...(airport && fields.direction === 'departing' ? ['returnAirline', 'returnFlightNumber'] : [])] : []),
        ],
    };
}

export function reservationErrors(fields, options = {}) {
    const rules = reservationRules(fields, options);
    const errors = {};
    for (const key of ['date', 'time', 'pickup', 'dropoff', 'airline', 'flightNumber', 'flightTime', 'fullName', 'mobile', 'email',
        'returnDate', 'returnTime', 'returnPickup', 'returnDropoff', 'returnAirline', 'returnFlightNumber', 'returnFlightTime']) {
        if (typeof fields[key] !== 'string') errors[key] = 'Enter text for this field';
    }
    for (const key of ['bookingForOther', 'roundtrip', 'sameLocation']) {
        if (typeof fields[key] !== 'boolean') errors[key] = 'Choose yes or no';
    }
    for (const key of rules.required) {
        if (fields[key] === null || fields[key] === undefined || String(fields[key]).trim() === '') errors[key] = 'Required';
    }
    if (!RESERVATION_SERVICES.some((s) => s.code === fields.serviceType)) errors.serviceType = 'Choose a service';
    for (const key of ['date', ...(fields.roundtrip ? ['returnDate'] : [])]) {
        const value = fields[key];
        const date = new Date(`${value}T12:00:00Z`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) errors[key] = 'Choose a valid date';
    }
    for (const key of ['time', ...(fields.roundtrip ? ['returnTime'] : [])]) {
        if (!normalizeTime(fields[key])) errors[key] = 'Choose a pickup time';
    }
    if (fields.roundtrip && `${fields.returnDate}T${fields.returnTime}` <= `${fields.date}T${fields.time}`) errors.returnTime = 'Return must be after the outbound pickup';
    if (!Number.isInteger(Number(fields.passengers)) || Number(fields.passengers) < 1 || Number(fields.passengers) > 99) errors.passengers = 'Use 1-99 passengers';
    for (const key of ['bags', 'childSeats']) {
        if (fields[key] !== '' && (!Number.isInteger(Number(fields[key])) || Number(fields[key]) < 0 || Number(fields[key]) > 99)) errors[key] = 'Use 0-99';
    }
    if (rules.charter && (!Number.isFinite(Number(fields.hours)) || Number(fields.hours) < rules.charterMinimumHours || Number(fields.hours) > 72)) errors.hours = `Minimum ${rules.charterMinimumHours} hours; maximum 72`;
    if (rules.contact && !/^\+?[\d ()-]{7,25}$/.test(fields.mobile || '')) errors.mobile = 'Enter a mobile number';
    if (rules.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email || '')) errors.email = 'Enter an email address';
    if (rules.airport) {
        if (!['arriving', 'departing'].includes(fields.direction)) errors.direction = 'Choose a direction';
        for (const key of ['flightTime', ...(fields.roundtrip ? ['returnFlightTime'] : [])]) {
            if (fields[key] && !normalizeTime(fields[key])) errors[key] = 'Choose a valid flight time';
        }
        for (const key of ['flightNumber', ...(fields.roundtrip ? ['returnFlightNumber'] : [])]) {
            if (fields[key] && !parseFlightNumber(fields[key])) errors[key] = 'Use airline code and flight number, e.g. DL123';
        }
    }
    for (const key of ['pickup', 'dropoff', 'returnPickup', 'returnDropoff']) {
        if ((rules.required.includes(key) && String(fields[key] || '').trim().length < 3) || String(fields[key] || '').length > 500) errors[key] = 'Enter a full address';
    }
    if (rules.extras && (!Array.isArray(fields.stops) || fields.stops.length > 3
        || fields.stops.some((stop) => typeof stop !== 'string' || stop.trim().length < 3 || stop.length > 500))) errors.stops = 'Enter up to 3 full stop addresses';
    if (typeof fields.notes !== 'string' || fields.notes.length > 2000) errors.notes = 'Use up to 2000 characters';
    return errors;
}

export function reservationPayload(fields, { accountId, requestId }) {
    const rules = reservationRules(fields);
    const flight = (returning = false) => {
        const direction = returning ? (fields.direction === 'arriving' ? 'departing' : 'arriving') : fields.direction;
        const prefix = returning ? 'return' : '';
        return {
            airline: fields[returning ? 'returnAirline' : 'airline'].trim(),
            number: fields[returning ? 'returnFlightNumber' : 'flightNumber'].trim(),
            direction, terminal: fields.terminal,
            flightTime: fields[prefix ? 'returnFlightTime' : 'flightTime'] || null,
            date: fields[prefix ? 'returnFlightDate' : 'flightDate'] || fields[prefix ? 'returnDate' : 'date'],
        };
    };
    return {
        requestId, accountId, serviceType: fields.serviceType,
        pickup: fields.pickup.trim(), dropoff: fields.dropoff.trim(), date: fields.date, time: fields.time,
        passengers: Number(fields.passengers), bags: fields.bags === '' ? 0 : Number(fields.bags),
        flight: rules.airport ? flight() : null, airportCode: rules.airport ? fields.airportCode : null,
        hours: rules.charter ? Number(fields.hours) : null,
        rider: fields.bookingForOther || fields.fullName ? {
            fullName: fields.fullName.trim(), mobile: fields.mobile.trim(), email: fields.email.trim(),
        } : null,
        stops: rules.extras ? fields.stops.map((stop) => stop.trim()) : [],
        childSeats: rules.extras ? Number(fields.childSeats) : 0, notes: [fields.notes, fields.riderNames ? `Riders: ${fields.riderNames}` : ''].filter(Boolean).join('\n').trim(),
        roundtrip: fields.roundtrip ? {
            sameLocation: fields.sameLocation, date: fields.returnDate, time: fields.returnTime,
            pickup: fields.sameLocation ? fields.dropoff.trim() : fields.returnPickup.trim(),
            dropoff: fields.sameLocation ? fields.pickup.trim() : fields.returnDropoff.trim(),
            flight: rules.airport ? flight(true) : null,
        } : null,
    };
}

const CAPTURE_KEYS = {
    serviceType: 'service_type', date: 'date', time: 'time', passengers: 'passenger_count',
    pickup: 'pickup_location', dropoff: 'dropoff_location', hours: 'hours', airportCode: 'airport_code',
    direction: 'flight_direction', airline: 'flight_info', flightNumber: 'flight_info',
    fullName: 'full_name', mobile: 'phone', email: 'email',
    returnDate: 'return_date', returnTime: 'return_time', returnPickup: 'return_pickup',
    returnDropoff: 'return_dropoff', returnAirline: 'return_flight_info', returnFlightNumber: 'return_flight_info',
};

export function reservationChecklistRequired(checklist = {}) {
    const value = (key) => checklist[key]?.value;
    const fields = {
        serviceType: value('service_type'), direction: value('flight_direction'),
        bookingForOther: value('booking_for_other') === true, roundtrip: value('roundtrip') === true,
        sameLocation: value('same_location') !== false,
    };
    return [...new Set(reservationRules(fields, { knownClient: true }).required.map((key) =>
        fields.bookingForOther && key === 'fullName' ? 'passenger_name'
            : fields.bookingForOther && key === 'mobile' ? 'passenger_phone' : CAPTURE_KEYS[key]).filter(Boolean))];
}

export function reservationServiceCode(value) {
    const name = String(value || '').trim().toLowerCase();
    return ({ ax: 'AX', 'airport transfer': 'AX', 'airport-transfer': 'AX',
        p2p: 'P2P', 'point-to-point': 'P2P', ch: 'CH', charter: 'CH' })[name] || '';
}

/** Adapts the existing Attendant checklist; it does not introduce another intake or booking engine. */
export function reservationFieldsFromChecklist(checklist = {}) {
    const value = (key, fallback = '') => checklist[key]?.value ?? fallback;
    const flight = parseFlightNumber(value('flight_info'));
    const returnFlight = parseFlightNumber(value('return_flight_info'));
    const other = value('booking_for_other', false) === true;
    return {
        ...EMPTY_RESERVATION,
        serviceType: reservationServiceCode(value('service_type')), direction: value('flight_direction'),
        airportCode: value('airport_code'), date: value('date'), time: normalizeTime(value('time')) || '',
        passengers: value('passenger_count', ''), bags: value('luggage_count', ''), hours: value('hours'),
        pickup: value('pickup_location'), dropoff: value('dropoff_location'),
        airline: flight?.airlineCode || '', flightNumber: flight?.flightNumber || '',
        flightTime: normalizeTime(value('flight_time')) || '',
        flightDate: checklist.time?.flightDate || '',
        bookingForOther: other, fullName: other ? value('passenger_name') : '',
        mobile: other ? value('passenger_phone') : '', email: '',
        roundtrip: value('roundtrip', false) === true, sameLocation: value('same_location', true) !== false,
        returnDate: value('return_date'), returnTime: normalizeTime(value('return_time')) || '',
        returnPickup: value('return_pickup'), returnDropoff: value('return_dropoff'),
        returnAirline: returnFlight?.airlineCode || '', returnFlightNumber: returnFlight?.flightNumber || '',
        returnFlightTime: normalizeTime(value('return_flight_time')) || '',
        returnFlightDate: checklist.return_time?.flightDate || '',
        stops: value('extra_stops', []), childSeats: value('child_seats', 0), notes: value('notes'),
    };
}
